import { useQuery } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { CACHE_CONSTANTS } from '../constants/cache.ts';
import { useTransactionService } from '../services/transaction.service';
import { QUERY_KEYS } from '../utils/queryKeys';
import {
    CoinWithSymbol,
    SpamCenterCoin,
    TransactionIssue,
    TransactionType,
    TransactionTypeValue,
} from '../services/transaction.types';

interface TransactionContextType {
    userCoins: CoinWithSymbol[];
    transactionTypes: TransactionType[];
    transactionTypesWithTransfer: TransactionType[];
    transactionIssues: TransactionIssue[];
    accounts: string[];
    userTypes: TransactionType[];
    
    spamCoins: SpamCenterCoin[];

    isLoading: boolean;
    error: Error | null;
    allTimeIssueCount: any;
}

const TransactionContext = createContext<TransactionContextType | null>(null);

function useTransaction() {
    const context = useContext(TransactionContext);
    if (!context) {
        throw new Error('useTransaction must be used within a TransactionProvider');
    }
    return context;
}

interface TransactionProviderProps {
    children: ReactNode;
}

const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
    const transactionService = useTransactionService();

    const {
        data: userCoins = [],
        isLoading: isUserCoinsLoading,
        error: userCoinsError,
    } = useQuery({
        queryKey: ['userCoins'],
        queryFn: transactionService.getUserCoins,
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
    });

    const {
        data: transactionTypesWithTransfer = [],
        isLoading: isTypesLoading,
        error: typesError,
    } = useQuery({
        queryKey: ['transactionTypes'],
        queryFn: transactionService.getTypes,
        staleTime: CACHE_CONSTANTS.STATIC_DATA_STALE_TIME,
    });

    const transactionTypes = useMemo(() => {
        return transactionTypesWithTransfer.filter((type) => type.value !== TransactionTypeValue.Transfer);
    }, [transactionTypesWithTransfer]);

    const {
        data: transactionIssues = [],
        isLoading: isIssuesLoading,
        error: issuesError,
    } = useQuery({
        queryKey: ['transactionIssues'],
        queryFn: transactionService.getTransactionIssues,
        staleTime: CACHE_CONSTANTS.STATIC_DATA_STALE_TIME,
    });

    const {
        data: accounts = [],
        isLoading: isAccountsLoading,
        error: accountsError,
    } = useQuery({
        queryKey: ['accounts'],
        queryFn: transactionService.getUserAccounts,
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
    });

    const {
        data: spamCoins = [],
        isLoading: isSpamCoinsLoading,
        error: spamCoinsError,
    } = useQuery({
        queryKey: ['spamCoins'],
        queryFn: transactionService.checkSpam,
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
    });

    const {
        data: userTypes = [],
        isLoading: isUserTypesLoading,
        error: userTypesError,
    } = useQuery({
        queryKey: ['userTypes'],
        queryFn: transactionService.getUserTypes,
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
    });

     const {
        data: allTimeIssueCount,
        isLoading: isAllTimeIssueCountLoading,
        error: allTimeIssueCountError,
    } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUE_COUNT],
        queryFn: () => transactionService.getAllTimeIssueCount(),
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
    });

    const value = {
        userCoins,
        transactionTypes,
        transactionTypesWithTransfer,
        transactionIssues,
        accounts,
        spamCoins,
        userTypes,
        allTimeIssueCount,
        isLoading:
            isUserCoinsLoading ||
            isTypesLoading ||
            isIssuesLoading ||
            isAccountsLoading ||
            isSpamCoinsLoading ||
            isUserTypesLoading ||
            isAllTimeIssueCountLoading,
        error: userCoinsError || typesError || issuesError || accountsError || spamCoinsError || userTypesError || allTimeIssueCountError,
    };

    return <TransactionContext.Provider value={value}>{children}</TransactionContext.Provider>;
};

export { TransactionProvider, useTransaction };
